import type React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '~/components/ui';
import { routes } from '~/app/routes';

export type PageActionKind = 'primary' | 'secondary' | 'danger';

export interface PageAction {
  label: string;
  kind: PageActionKind;
  /** Required — no slugify fallback. */
  testId: string;
  /** Exactly one of `onClick` | `href`. */
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  startIcon?: React.ReactNode;
}

export interface PageHeaderProps {
  title: string;
  /** One muted sentence under the breadcrumbs saying what the page is for. */
  description?: string;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
  }>;
  actions?: PageAction[];
  /** Non-action widgets, rendered left of the button group. */
  extra?: React.ReactNode;
  testId?: string;
}

// §2: destructive, then secondary, then primary, left to right.
const KIND_ORDER: Record<PageActionKind, number> = { danger: 0, secondary: 1, primary: 2 };
const KIND_TO_VARIANT: Record<PageActionKind, 'primary' | 'outline' | 'danger'> = {
  primary: 'primary',
  secondary: 'outline',
  danger: 'danger',
};

function ActionControl({ action }: { action: PageAction }) {
  const variant = KIND_TO_VARIANT[action.kind];

  return (
    <Button
      variant={variant}
      size="sm"
      onClick={action.onClick}
      href={action.href}
      disabled={action.disabled}
      startIcon={action.startIcon}
      testId={action.testId}
    >
      {action.label}
    </Button>
  );
}

/**
 * PageHeader - Standard page header with title, breadcrumbs and the page's §2 action group.
 * Actions are a typed descriptor array so ordering and the one-primary rule
 * are structural rather than a review comment on a freeform node.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  actions,
  extra,
  testId,
}) => {
  const sortedActions = actions
    ? [...actions].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
    : [];

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6" data-testid={testId}>
      <div>
        {/* The page's own title, distinct from any heading a card or drawer renders below
            it — every screen has exactly one, which is what makes it addressable. */}
        <h2
          className="text-xl font-semibold text-gray-800 dark:text-white/90"
          data-testid="page-title"
        >
          {title}
        </h2>

        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb">
            <ol className="mt-1.5 flex items-center gap-1.5">
              <li>
                <Link
                  className="inline-flex items-center gap-1.5 rounded-sm text-sm text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-gray-300 dark:focus-visible:ring-offset-gray-900"
                  to={routes.dashboard}
                >
                  Dashboard
                  <ChevronRight className="size-4" />
                </Link>
              </li>
              {breadcrumbs.map((crumb, index) => (
                <li key={index}>
                  {crumb.href && index < breadcrumbs.length - 1 ? (
                    <Link
                      className="inline-flex items-center gap-1.5 rounded-sm text-sm text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-gray-300 dark:focus-visible:ring-offset-gray-900"
                      to={crumb.href}
                    >
                      {crumb.label}
                      <ChevronRight className="size-4" />
                    </Link>
                  ) : index < breadcrumbs.length - 1 ? (
                    // An ancestor with no page of its own (a sidebar group): muted, still followed by a separator.
                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                      {crumb.label}
                      <ChevronRight className="size-4" aria-hidden="true" />
                    </span>
                  ) : (
                    <span aria-current="page" className="text-sm text-gray-800 dark:text-white/90">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        {description && (
          <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>

      {(extra || sortedActions.length > 0) && (
        <div className="flex items-center gap-3">
          {extra}
          {sortedActions.length > 0 && (
            <div className="flex items-center gap-3" role="group" aria-label="Page actions">
              {sortedActions.map((action) => (
                <ActionControl key={action.testId} action={action} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
